import { FC, Fragment, useState } from "react";
import { Sell } from "@mui/icons-material";
import { useAccount, useSendTransaction } from "wagmi";

import { useApiCall } from "@dzambalaorg/react-hooks";
import { useMetamask } from "@dzambalaorg/react-hooks-eth";
import { emptyPrice } from "@dzambalaorg/mui-inputs-asset";
import { ListAction, ListActionVariant } from "@framework/styled";
import { ContractFeatures, type IToken, OpenSea16SupportedChains, TokenStatus } from "@framework/types";
import { NodeEnv } from "@dzambalaorg/constants";
import { ZeroAddress } from "@framework/constants";

import { type ISellDto, SellDialog } from "./dialog";

interface IOpenSeaSellButtonProps {
  className?: string;
  disabled?: boolean;
  token: IToken;
  variant?: ListActionVariant;
}

export const OpenSeaSellButton: FC<IOpenSeaSellButtonProps> = props => {
  const { className, disabled, token, variant = ListActionVariant.button } = props;
  const referrer = ZeroAddress;
  const { address: account, chainId = 0 } = useAccount();
  const { sendTransaction } = useSendTransaction();

  const [isSellDialogOpen, setIsSellDialogOpen] = useState(false);

  const { execute: metaFn } = useMetamask(async (dto: Array<any>) => {
    return Promise.all(
      dto.map(async tx => {
        return sendTransaction(tx);
      }),
    );
  });

  const { fn } = useApiCall(
    async (api, values: ISellDto) => {
      return api.fetchJson({
        url: `/marketplace/sell`,
        method: "POST",
        data: {
          chainId,
          account,
          referrer,
          tokenId: token.id,
          amount: 1,
          endTimestamp: values.endTimestamp,
          price: {
            components: [
              {
                tokenType: values.price.components[0].tokenType,
                contractId: values.price.components[0].contractId,
                templateId: values.price.components[0].templateId,
                amount: values.price.components[0].amount,
              },
            ],
          },
        },
      });
    },
    { success: false },
  );

  const handleSellConfirm = async (values: ISellDto, form: any) => {
    return fn(form, values)
      .then(async resp => {
        return metaFn(resp);
      })
      .finally(() => {
        setIsSellDialogOpen(false);
      });
  };

  const handleSell = () => {
    setIsSellDialogOpen(true);
  };

  const handleSellCancel = () => {
    setIsSellDialogOpen(false);
  };

  // TIME + 1 day from Now
  const date = new Date();
  date.setDate(date.getDate() + 1);

  if (process.env.NODE_ENV === NodeEnv.production) {
    if (!Object.values(OpenSea16SupportedChains).includes(chainId)) {
      return null;
    }
  }

  if (token.tokenStatus === TokenStatus.BURNED) {
    return null;
  }

  return (
    <Fragment>
      <ListAction
        icon={Sell}
        onClick={handleSell}
        message="form.buttons.sell"
        className={className}
        dataTestId="OpenSeaSellButton"
        disabled={disabled || token.template?.contract?.contractFeatures.includes(ContractFeatures.SOULBOUND)}
        variant={variant}
      />
      <SellDialog
        onConfirm={handleSellConfirm}
        onCancel={handleSellCancel}
        open={isSellDialogOpen}
        initialValues={{
          price: emptyPrice,
          endTimestamp: date.toISOString(),
        }}
      />
    </Fragment>
  );
};
